/**
 * Telugu Language Detection and Typography Hook
 * Automatically detects Telugu content and applies appropriate typography
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import { useState, useEffect, useMemo } from 'react';
import { SupportedLanguage } from '../types/aiTypes';

// ================== TELUGU DETECTION UTILITIES ==================

/**
 * Telugu Unicode ranges for script detection
 */
const TELUGU_UNICODE_RANGES = [
  [0x0C00, 0x0C7F], // Telugu block
  [0x0C80, 0x0CFF], // Telugu extended block (if needed)
];

/**
 * Common Telugu words for language detection
 */
const TELUGU_COMMON_WORDS = [
  'అని', 'ఇది', 'అది', 'ఈ', 'ఆ', 'ఉన్నది', 'చేసిన', 'కలిగిన',
  'మరియు', 'లేదా', 'కానీ', 'అయితే', 'అంటే', 'వంటి', 'గురించి',
  'చేయడం', 'అవుతుంది', 'చేస్తుంది', 'ఉంది', 'లేదు', 'కాదు'
];

/**
 * Detects if text contains Telugu characters
 */
export const isTeluguText = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false;
  
  // Check for Telugu Unicode characters
  for (const char of text) {
    const code = char.charCodeAt(0);
    for (const [start, end] of TELUGU_UNICODE_RANGES) {
      if (code >= start && code <= end) {
        return true;
      }
    }
  }
  
  return false;
};

/**
 * Estimates the percentage of Telugu content in text
 */
export const getTeluguContentPercentage = (text: string): number => {
  if (!text || typeof text !== 'string') return 0;
  
  let teluguCharCount = 0;
  let totalCharCount = 0;
  
  for (const char of text) {
    // Skip whitespace and punctuation
    if (/\s|[.,!?;:]/.test(char)) continue;
    
    totalCharCount++;
    const code = char.charCodeAt(0);
    
    for (const [start, end] of TELUGU_UNICODE_RANGES) {
      if (code >= start && code <= end) {
        teluguCharCount++;
        break;
      }
    }
  }
  
  return totalCharCount > 0 ? (teluguCharCount / totalCharCount) * 100 : 0;
};

/**
 * Detects language based on content analysis
 */
export const detectContentLanguage = (text: string): SupportedLanguage => {
  if (!text || typeof text !== 'string') return 'English';
  
  const teluguPercentage = getTeluguContentPercentage(text);
  
  // If more than 50% Telugu characters, consider it Telugu
  if (teluguPercentage > 50) {
    return 'Telugu';
  }
  
  // Check for common Telugu words
  const words = text.toLowerCase().split(/\s+/);
  const teluguWordCount = words.filter(word => 
    TELUGU_COMMON_WORDS.some(teluguWord => word.includes(teluguWord))
  ).length;
  
  const teluguWordPercentage = words.length > 0 ? (teluguWordCount / words.length) * 100 : 0;
  
  if (teluguWordPercentage > 30) {
    return 'Telugu';
  }
  
  // Default to English if not clearly Telugu
  return 'English';
};

// ================== TYPOGRAPHY HOOK ==================

export interface UseTeluguTypographyReturn {
  isTeluguContent: boolean;
  contentLanguage: SupportedLanguage;
  typographyClasses: string;
  titleClasses: string;
  bodyClasses: string;
  descriptionClasses: string;
  chatMessageClasses: string;
  applyTeluguStyling: (element: HTMLElement | null) => void;
  removeTeluguStyling: (element: HTMLElement | null) => void;
}

export interface UseTeluguTypographyOptions {
  content?: string;
  language?: SupportedLanguage;
  autoDetect?: boolean;
  variant?: 'article' | 'chat' | 'form' | 'display';
  serif?: boolean;
}

/**
 * Hook for automatic Telugu typography detection and application
 */
export const useTeluguTypography = (options: UseTeluguTypographyOptions = {}): UseTeluguTypographyReturn => {
  const {
    content = '',
    language,
    autoDetect = true,
    variant = 'article',
    serif = false
  } = options;

  const [detectedLanguage, setDetectedLanguage] = useState<SupportedLanguage>('English');

  // Detect language when content changes
  useEffect(() => {
    if (autoDetect && content) {
      const detected = detectContentLanguage(content);
      setDetectedLanguage(detected);
    }
  }, [content, autoDetect]);

  // Determine final language
  const contentLanguage = language || (autoDetect ? detectedLanguage : 'English');
  const isTeluguContent = contentLanguage === 'Telugu' || isTeluguText(content);

  // Generate CSS classes based on configuration
  const typographyClasses = useMemo(() => {
    if (!isTeluguContent) return '';

    const classes = ['telugu-content'];
    
    if (variant === 'article') {
      classes.push('telugu-article');
    }
    
    if (serif) {
      classes.push('telugu-serif');
    }

    return classes.join(' ');
  }, [isTeluguContent, variant, serif]);

  const titleClasses = useMemo(() => {
    if (!isTeluguContent) return '';
    
    if (serif) {
      return 'telugu-serif telugu-serif-title';
    }
    
    return variant === 'display' 
      ? 'telugu-headline telugu-headline-1'
      : 'telugu-headline telugu-headline-2';
  }, [isTeluguContent, variant, serif]);

  const bodyClasses = useMemo(() => {
    if (!isTeluguContent) return '';
    
    if (serif) {
      return 'telugu-serif telugu-serif-body';
    }
    
    return variant === 'article' 
      ? 'telugu-body'
      : 'telugu-body telugu-body-large';
  }, [isTeluguContent, variant, serif]);

  const descriptionClasses = useMemo(() => {
    if (!isTeluguContent) return '';
    return 'telugu-body telugu-body-large';
  }, [isTeluguContent]);

  const chatMessageClasses = useMemo(() => {
    if (!isTeluguContent) return '';
    return 'telugu-content telugu-message';
  }, [isTeluguContent]);

  // Utility functions to apply/remove styling
  const applyTeluguStyling = (element: HTMLElement | null) => {
    if (!element || !isTeluguContent) return;
    
    element.classList.add('telugu-content');
    
    // Apply specific styles based on element type
    if (element.tagName.toLowerCase().match(/^h[1-6]$/)) {
      element.classList.add('telugu-headline');
    } else if (element.tagName.toLowerCase() === 'p') {
      element.classList.add('telugu-body');
    }
  };

  const removeTeluguStyling = (element: HTMLElement | null) => {
    if (!element) return;
    
    element.classList.remove(
      'telugu-content', 
      'telugu-article', 
      'telugu-headline', 
      'telugu-body', 
      'telugu-serif'
    );
  };

  return {
    isTeluguContent,
    contentLanguage,
    typographyClasses,
    titleClasses,
    bodyClasses,
    descriptionClasses,
    chatMessageClasses,
    applyTeluguStyling,
    removeTeluguStyling
  };
};

export default useTeluguTypography;
